if (!customElements.get('nb-ugc-carousel')) {
  customElements.define(
    'nb-ugc-carousel',
    class NbUgcCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.nb-ugc-carousel__track');
        this.items = this.querySelectorAll('.nb-ugc-carousel__media');
        this.items.forEach((item) => {
          const playButton = item.querySelector('[data-nb-ugc-play]');
          const muteButton = item.querySelector('[data-nb-ugc-mute]');
          const video = item.querySelector('video');
          if (playButton && video) {
            playButton.addEventListener('click', (event) => {
              if (this.suppressNextClick) return;
              this.togglePlay(playButton, video);
            });
          }
          if (muteButton && video) {
            muteButton.addEventListener('click', (event) => {
              event.stopPropagation();
              if (this.suppressNextClick) return;
              this.toggleMute(muteButton, video);
            });
          }
        });

        this.bindDrag();
        this.observeViewport();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      bindDrag() {
        if (!this.track) return;
        // Threshold (px) before a pointer-down counts as a drag rather than a click.
        const DRAG_THRESHOLD = 5;
        let isDown = false;
        let didDrag = false;
        let startX = 0;
        let startScroll = 0;
        let activePointerId = null;

        const onPointerDown = (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          isDown = true;
          didDrag = false;
          startX = event.clientX;
          startScroll = this.track.scrollLeft;
          activePointerId = event.pointerId;
        };

        const onPointerMove = (event) => {
          if (!isDown || event.pointerId !== activePointerId) return;
          const delta = event.clientX - startX;
          if (!didDrag && Math.abs(delta) > DRAG_THRESHOLD) {
            didDrag = true;
            this.track.classList.add('is-dragging');
            try {
              this.track.setPointerCapture(activePointerId);
            } catch (e) {}
          }
          if (didDrag) {
            event.preventDefault();
            this.track.scrollLeft = startScroll - delta;
          }
        };

        const endDrag = (event) => {
          if (!isDown || (event && event.pointerId !== activePointerId)) return;
          isDown = false;
          if (didDrag) {
            // Suppress the click that fires after a drag-release on the underlying play button.
            this.suppressNextClick = true;
            window.setTimeout(() => {
              this.suppressNextClick = false;
            }, 0);
            this.track.classList.remove('is-dragging');
            try {
              this.track.releasePointerCapture(activePointerId);
            } catch (e) {}
          }
          activePointerId = null;
        };

        this.track.addEventListener('pointerdown', onPointerDown);
        this.track.addEventListener('pointermove', onPointerMove);
        this.track.addEventListener('pointerup', endDrag);
        this.track.addEventListener('pointercancel', endDrag);
        this.track.addEventListener('dragstart', (event) => event.preventDefault());
      }

      observeViewport() {
        const firstItem = this.items[0];
        if (!firstItem || !('IntersectionObserver' in window)) return;
        const playButton = firstItem.querySelector('[data-nb-ugc-play]');
        const video = firstItem.querySelector('video');
        if (!playButton || !video) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.autoplayFirst(playButton, video);
                this.observer.disconnect();
              }
            });
          },
          { threshold: 0.4 }
        );
        this.observer.observe(this);
      }

      autoplayFirst(button, video) {
        if (!video.paused) return;
        const result = video.play();
        if (result && typeof result.then === 'function') {
          result
            .then(() => {
              button.dataset.playing = 'true';
              button.setAttribute('aria-label', 'Pause video');
            })
            .catch(() => {});
        } else {
          button.dataset.playing = 'true';
          button.setAttribute('aria-label', 'Pause video');
        }
      }

      togglePlay(button, video) {
        if (video.paused) {
          this.items.forEach((other) => {
            const otherVideo = other.querySelector('video');
            const otherButton = other.querySelector('[data-nb-ugc-play]');
            if (otherVideo && otherVideo !== video && !otherVideo.paused) {
              otherVideo.pause();
              if (otherButton) otherButton.dataset.playing = 'false';
            }
          });
          video.play();
          button.dataset.playing = 'true';
          button.setAttribute('aria-label', 'Pause video');
        } else {
          video.pause();
          button.dataset.playing = 'false';
          button.setAttribute('aria-label', 'Play video');
        }
      }

      toggleMute(button, video) {
        const nextMuted = !video.muted;
        if (!nextMuted) {
          this.items.forEach((other) => {
            const otherVideo = other.querySelector('video');
            const otherMute = other.querySelector('[data-nb-ugc-mute]');
            if (otherVideo && otherVideo !== video && !otherVideo.muted) {
              otherVideo.muted = true;
              if (otherMute) {
                otherMute.setAttribute('aria-pressed', 'true');
                otherMute.setAttribute('aria-label', 'Unmute video');
              }
            }
          });
        }
        video.muted = nextMuted;
        button.setAttribute('aria-pressed', nextMuted ? 'true' : 'false');
        button.setAttribute('aria-label', nextMuted ? 'Unmute video' : 'Mute video');
      }
    }
  );
}
